import { Injectable } from "@angular/core";
import { CookieService } from "ngx-cookie-service";
import { OAuthService } from "../../../../../lib/auth/oauth/oauth.service";
import { UrlHelper } from "../../../../../lib/helpers/url.helper";
import { DISPLAYED_USER_MESSAGES_COOKIE, UserMessageData } from "../models/user-message.model";

interface DisplayedMessagesCookie {
  /** SSO session id (the token `sso` claim) the acknowledgements belong to. */
  sid: string;
  /** Fingerprints of popups already shown in that session. */
  ids: string[];
}

/**
 * Tracks which popup messages have already been shown so they are not
 * re-displayed on every guarded navigation.
 *
 * The acknowledgement set is stored in a cookie scoped to the registrable
 * domain (e.g. `.levelesq.net`) instead of `sessionStorage`, because
 * `sessionStorage` is per-origin: the cross-subdomain "Fund a Case" redirect
 * (my.levelesq.net -> portal.levelesq.net, via a full `window.location` load)
 * landed on a fresh origin and re-showed every popup (RYNO-14713).
 *
 * "Session" is defined as the **SSO login session**, not the (ill-defined)
 * browser-cookie session: the cookie is stamped with the current `sso` id, and
 * a set stamped with any other id (previous login, or a different user on the
 * same browser) is ignored. Acknowledgements therefore reset exactly when the
 * login session changes, regardless of cookie lifetime quirks.
 *
 * This sid-stamping is the *only* reset mechanism — the cookie is deliberately
 * NOT cleared on logout/session-end. `OAuthService.sessionEndLocal` runs per-tab
 * (including in the transient tokenless window when the app is opened in a fresh
 * tab), but the cookie is shared across every tab and subdomain; clearing it
 * there would re-show popups in all other open tabs. A genuine login change
 * already invalidates old acks via the `sso` mismatch, so no explicit clear is
 * needed.
 */
@Injectable({
  providedIn: "root",
})
export class UserMessageStorageService {
  constructor(
    private readonly cookieService: CookieService,
    private readonly oauthService: OAuthService,
  ) {}

  getMessageFingerprint(message: UserMessageData): string {
    return `${message.id}_${message.isEntityMessage ? 'entity' : 'global'}`;
  }

  markMessageAsDisplayed(message: UserMessageData): void {
    const sid = this.oauthService.getSessionId();
    const ids = this.getDisplayedFingerprints(sid);
    const fingerprint = this.getMessageFingerprint(message);
    if (!ids.includes(fingerprint)) {
      ids.push(fingerprint);
      const cookie: DisplayedMessagesCookie = { sid, ids };
      this.cookieService.set(DISPLAYED_USER_MESSAGES_COOKIE, JSON.stringify(cookie), this.getCookieOptions());
    }
  }

  filterNewMessages(messages: UserMessageData[]): UserMessageData[] {
    if (!messages || messages.length === 0) {
      return [];
    }
    const ids = this.getDisplayedFingerprints(this.oauthService.getSessionId());
    return messages.filter(message => !ids.includes(this.getMessageFingerprint(message)));
  }

  /** Fingerprints already shown in the given SSO session; empty for any other (or no) session. */
  private getDisplayedFingerprints(sid: string): string[] {
    const stored = this.cookieService.get(DISPLAYED_USER_MESSAGES_COOKIE);
    if (!stored) {
      return [];
    }
    try {
      const parsed = JSON.parse(stored) as DisplayedMessagesCookie;
      if (!parsed || parsed.sid !== sid || !Array.isArray(parsed.ids)) {
        return [];
      }
      return parsed.ids;
    } catch {
      return [];
    }
  }

  private getCookieOptions(): { path: string; domain?: string; secure: boolean; sameSite: "Lax" } {
    return {
      path: "/",
      domain: UrlHelper.getCookieDomain(),
      secure: window.location.protocol === "https:",
      sameSite: "Lax",
    };
  }
}
